'use client';

import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { parseItemDetails } from '../utils/formatters';
import { SkinImage } from './ui/SkinImage';
import { cn } from '../utils/cn';
import { rarityGradient } from '../utils/rarityGradient';
import { useLocalTranslation } from '../hooks/useLocalTranslation';
import translations from './ItemCard.translations';

interface ItemCardProps {
  imageUrl: string;
  name: string;
  /** Rarity label (e.g. "Classified"). Rendered as the pass tagline. */
  rarity?: string;
  /** Rarity color hex (e.g. "#d32ce6"). Drives the pass background gradient. */
  rarityColor?: string;
  /** @deprecated The pass-style card no longer renders price inside; render it separately (e.g. in a primary CTA). Kept for back-compat — value is ignored. */
  rightContent?: ReactNode;
  className?: string;
}

/**
 * Apple Wallet pass-style item card.
 *
 * Pattern: rarity-colored card, tagline (rarity name) top-left, badges
 * (StatTrak/Souvenir/★) top-right, centered skin image, primary skin name,
 * secondary WEAPON/CONDITION field grid divided by a hairline.
 *
 * The API matches the previous flat ItemCard (imageUrl/name/rarity/rarityColor/
 * rightContent) so all call sites keep working. WeaponName / skinName /
 * condition / quality flags are derived from `name` via parseItemDetails.
 */
type KindKey =
  | 'weapon'
  | 'sticker'
  | 'graffiti'
  | 'case'
  | 'capsule'
  | 'package'
  | 'collection'
  | 'musicKit'
  | 'patch'
  | 'charm'
  | 'agent'
  | 'knife'
  | 'gloves'
  | 'item';

/**
 * Kinds where parseItemDetails sets weaponName to the type literal
 * ("Sticker", "Music Kit", "Charm" etc.). Showing "Стикер: Sticker"
 * is redundant — caller should hide the weapon field for these.
 */
const REDUNDANT_KINDS: ReadonlySet<KindKey> = new Set(['sticker', 'graffiti', 'musicKit', 'patch', 'charm', 'case', 'capsule', 'package', 'collection']);

/** Suffix of `weaponName` that names a container kind — covers both a plain "Case"/
 *  "Capsule" (parseItemDetails' own case-match sets weaponName to exactly that word)
 *  and a compound one like "Autograph Capsule" or "Souvenir Package" (weaponName is
 *  just the pipe-split first segment there, so an exact-equality switch missed it —
 *  #178). */
const CONTAINER_SUFFIX_TO_KIND: ReadonlyArray<[RegExp, KindKey]> = [
  [/Case$/, 'case'],
  [/Capsule$/, 'capsule'],
  [/Package$/, 'package'],
  [/Collection$/, 'collection'],
];

const inferKindKey = (raw: string, weaponName: string, isStar: boolean, hasCondition: boolean): KindKey => {
  if (raw.startsWith('Sealed Graffiti')) return 'graffiti';
  if (raw.startsWith('Sticker')) return 'sticker';
  // Music Kit / Patch / Charm read off `weaponName`, not `raw`: parseItemDetails
  // already strips the StatTrak™/Souvenir prefix into it, so a "StatTrak™ Music Kit"
  // or a "Souvenir Charm" (event highlight charms) still matches here instead of
  // falling through to the agent guess below (#178).
  if (weaponName === 'Music Kit') return 'musicKit';
  if (weaponName === 'Patch') return 'patch';
  if (weaponName === 'Charm') return 'charm';
  const container = CONTAINER_SUFFIX_TO_KIND.find(([suffix]) => suffix.test(weaponName));
  if (container) return container[1];
  if (/Gloves|Wraps/i.test(weaponName)) return 'gloves';
  if (isStar) return 'knife';
  if (!weaponName) return 'item';
  // Agents are "Name | Faction" with no wear condition — the only kind left standing
  // once every other no-wear, pipe-containing item above has claimed its own branch.
  if (!hasCondition && raw.includes(' | ')) return 'agent';
  return 'weapon';
};

export const ItemCard: FC<ItemCardProps> = ({
  imageUrl,
  name,
  rarity,
  rarityColor = '#666666',
  rightContent: _rightContent,
  className,
}) => {
  const { t } = useLocalTranslation(translations);
  const { weaponName, skinName, condition, isStar, isStatTrak, isSouvenir } = useMemo(
    () => parseItemDetails(name),
    [name],
  );
  const kindKey = useMemo(
    () => inferKindKey(name, weaponName, isStar, Boolean(condition)),
    [name, weaponName, isStar, condition],
  );
  const isRedundant = REDUNDANT_KINDS.has(kindKey);
  const conditionKey = (kindKey === 'sticker' || kindKey === 'graffiti') && condition ? 'color' : 'condition';

  return (
    <div
      className={cn('rounded-3xl p-5 relative overflow-hidden text-white [text-shadow:0_2px_4px_rgba(0,0,0,0.8)]', className)}
      style={{
        background: rarityGradient(rarityColor),
      }}
    >
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 22% 12%, rgba(255,255,255,0.55), transparent 55%)' }}
      />
      <div className="relative">
        {/* Top row: rarity tagline + quality badges. */}
        <div className="flex items-center justify-between gap-2 min-h-[14px]">
          <span className="text-[11px] leading-[13px] font-semibold uppercase tracking-[1.2px] text-white/90 truncate">
            {rarity ?? ''}
          </span>
          <div className="flex items-center gap-1.5 shrink-0">
            {isStar && <span className="text-sm font-bold text-amber-200">★</span>}
            {isStatTrak && <PassBadge tone="orange">StatTrak™</PassBadge>}
            {isSouvenir && <PassBadge tone="orange">Souvenir</PassBadge>}
          </div>
        </div>

        {/* Centered skin image with a single soft drop shadow (no grounding pool). */}
        <div className="h-[220px] flex items-center justify-center mt-2">
          <SkinImage
            name={name}
            src={imageUrl}
            className="max-w-[90%]"
            imgClassName="max-h-[220px] w-full object-contain [filter:drop-shadow(0_8px_16px_rgba(0,0,0,0.55))]"
          />
        </div>

        {/* Primary field — skin name. */}
        <div className="text-[28px] leading-[34px] font-bold tracking-[-0.4px] mt-3 truncate">
          {skinName || weaponName}
        </div>

        {/* Secondary field grid — WEAPON / CONDITION. Price is intentionally
            NOT rendered on the card; show it in the primary CTA instead. */}
        {((weaponName && !isRedundant) || condition) && (
          <div className="flex items-end justify-between gap-3 mt-3 pt-3 border-t border-white/20">
            {weaponName && !isRedundant && <PassField label={t(kindKey)} value={weaponName} />}
            {condition && <PassField label={t(conditionKey)} value={condition} alignEnd={!isRedundant} />}
          </div>
        )}
      </div>
    </div>
  );
};

const PassField: FC<{ label: string; value: ReactNode; alignEnd?: boolean }> = ({ label, value, alignEnd }) => (
  <div className={cn('flex flex-col min-w-0', alignEnd ? 'items-end text-end' : 'items-start text-start')}>
    <span className="text-[10px] leading-[12px] font-semibold uppercase tracking-[1.2px] text-white/75">
      {label}
    </span>
    <span className="text-[17px] leading-[22px] font-semibold tracking-[-0.43px] mt-1 truncate max-w-full">
      {value}
    </span>
  </div>
);

const PassBadge: FC<{ tone: 'orange' | 'yellow'; children: ReactNode }> = ({ tone, children }) => (
  <span
    className={cn(
      'text-[10px] leading-[14px] tracking-[0.4px] font-bold uppercase px-1.5 py-0.5 rounded-md',
      tone === 'orange' ? 'bg-orange-500 text-white' : 'bg-yellow-300 text-black',
    )}
  >
    {children}
  </span>
);
